/**
 * AutomationsPanel — builder automatyzacji modułu Kreatora (F12.D2, Faza A).
 *
 * Port z ITE Workflow365 (zakładka „Automatyzacje"): lista reguł + edytor
 * (nazwa, wyzwalacze trigger→ multi + AND/OR, akcje, aktywność, limit wykonań).
 * CRUD: /api/modules/{id}/automations. Silnik odpala się przy zapisie rekordu
 * (apps/api/lib/module-automations/engine.ts).
 *
 * Faza A: triggery zdarzeniowe + rdzeń akcji (ustaw pole / przypisz właściciela /
 * e-mail / powiadomienie / webhook). Warunki, harmonogram, kolejne akcje — fazy B–D.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AUTOMATION_CONDITION_OPERATORS,
  type AutomationAction,
  type AutomationActionType,
  type AutomationCondition,
  type AutomationConditionOperator,
  type AutomationExecutionLimit,
  type AutomationTrigger,
  type AutomationTriggerType,
  type ModuleAutomationDTO,
  type ModuleAutomationLogDTO,
} from '@saas/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { Copy, Pause, Play, Plus, Trash2, Wand2, Zap } from 'lucide-react';

import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Dropdown } from '@/components/Dropdown';
import type { ModuleField } from '@/components/fields/FieldRegistry';
import { UserPicker } from '@/components/ui/UserPicker';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import { fetchProcessesForModule } from '@/lib/processes/instance-api';

// Mapy modułowe trzymają KLUCZE i18n (są poza komponentem — brak dostępu do `t`);
// tłumaczenie dzieje się dopiero w miejscu użycia przez `t(MAP[x])`.
const TRIGGER_LABELS: Record<AutomationTriggerType, string> = {
  entity_created: 'moduleAutomations.triggers.entity_created',
  entity_updated: 'moduleAutomations.triggers.entity_updated',
  entity_deleted: 'moduleAutomations.triggers.entity_deleted',
  field_updated: 'moduleAutomations.triggers.field_updated',
  field_equals: 'moduleAutomations.triggers.field_equals',
  status_changed: 'moduleAutomations.triggers.status_changed',
  // Czasowe (cron):
  stale_entity: 'moduleAutomations.triggers.stale_entity',
  due_date_approaching: 'moduleAutomations.triggers.due_date_approaching',
  due_date_passed: 'moduleAutomations.triggers.due_date_passed',
  time_based: 'moduleAutomations.triggers.time_based',
};

const ACTION_LABELS: Record<AutomationActionType, string> = {
  update_field: 'moduleAutomations.actions.update_field',
  assign_owner: 'moduleAutomations.actions.assign_owner',
  send_email: 'moduleAutomations.actions.send_email',
  send_notification: 'moduleAutomations.actions.send_notification',
  webhook: 'moduleAutomations.actions.webhook',
  // Faza D:
  create_record: 'moduleAutomations.actions.create_record',
  start_approval: 'moduleAutomations.actions.start_approval',
  add_tag: 'moduleAutomations.actions.add_tag',
  remove_tag: 'moduleAutomations.actions.remove_tag',
  archive: 'moduleAutomations.actions.archive',
  unarchive: 'moduleAutomations.actions.unarchive',
};

/** Wyzwalacze czasowe — skanowane cyklicznie przez cron (nie po zapisie rekordu). */
const TIME_TRIGGERS: AutomationTriggerType[] = [
  'stale_entity',
  'due_date_approaching',
  'due_date_passed',
  'time_based',
];
/** Wyzwalacze czasowe wskazujące pole daty. */
const DUE_DATE_TRIGGERS: AutomationTriggerType[] = ['due_date_approaching', 'due_date_passed'];

const EXECUTION_LIMIT_LABELS: Record<AutomationExecutionLimit, string> = {
  once: 'moduleAutomations.executionLimits.once',
  daily: 'moduleAutomations.executionLimits.daily',
  weekly: 'moduleAutomations.executionLimits.weekly',
  always: 'moduleAutomations.executionLimits.always',
};

const OPERATOR_LABELS: Record<AutomationConditionOperator, string> = {
  '==': 'moduleAutomations.operators.eq',
  '!=': 'moduleAutomations.operators.neq',
  '>': 'moduleAutomations.operators.gt',
  '<': 'moduleAutomations.operators.lt',
  '>=': 'moduleAutomations.operators.gte',
  '<=': 'moduleAutomations.operators.lte',
  in: 'moduleAutomations.operators.in',
  contains: 'moduleAutomations.operators.contains',
  is_null: 'moduleAutomations.operators.isNull',
  is_not_null: 'moduleAutomations.operators.isNotNull',
};
/** Operatory unarne — bez pola wartości. */
const VALUELESS_OPERATORS: AutomationConditionOperator[] = ['is_null', 'is_not_null'];

/** Wyzwalacze, które potrzebują wyboru pola. */
const FIELD_TRIGGERS: AutomationTriggerType[] = ['field_updated', 'field_equals', 'status_changed'];

/**
 * Element listy draftu z UI-owym, stabilnym id (`_uid`) do kluczy React — dzięki niemu
 * usunięcie elementu ze środka listy nie „przeskakuje" wartości kontrolowanych pól na
 * sąsiedni wiersz. `_uid` jest TYLKO dla UI: backend (Zod bez `.strict()`) obcina nieznane
 * pola przy zapisie, więc nie trafia ani do payloadu, ani do bazy.
 */
type Keyed<T> = T & { _uid?: string };

interface Draft {
  id?: string;
  name: string;
  description: string;
  isActive: boolean;
  triggers: Keyed<AutomationTrigger>[];
  triggerLogic: 'AND' | 'OR';
  /** Warunki-liście (pole/operator/wartość) łączone przez `conditionLogic`. */
  conditions: Keyed<AutomationCondition>[];
  conditionLogic: 'AND' | 'OR';
  actions: Keyed<AutomationAction>[];
  executionLimit: AutomationExecutionLimit;
  priority: number;
}

/** Serializuje warunki draftu do `conditionGroups` (jedna grupa) dla API/silnika. */
function conditionsToGroups(conditions: AutomationCondition[], logic: 'AND' | 'OR') {
  const clean = conditions.filter((c) => c.field && c.operator);
  return clean.length ? [{ logic, children: clean }] : [];
}

let uidSeq = 0;
/** Kolejne stabilne id do klucza React (tylko UI). */
const nextUid = () => `k${(uidSeq += 1)}`;

/** Nadaje `_uid` elementom list draftu, które go jeszcze nie mają (wczytanie/preselekt/szablon). */
function withUids(draft: Draft): Draft {
  const stamp = <T,>(x: Keyed<T>): Keyed<T> => (x._uid ? x : { ...x, _uid: nextUid() });
  return {
    ...draft,
    triggers: draft.triggers.map(stamp),
    conditions: draft.conditions.map(stamp),
    actions: draft.actions.map(stamp),
  };
}

function emptyDraft(): Draft {
  return {
    name: '',
    description: '',
    isActive: false,
    triggers: [{ trigger_type: 'entity_created', trigger_config: {} }],
    triggerLogic: 'AND',
    conditions: [],
    conditionLogic: 'AND',
    actions: [{ type: 'send_notification', config: {} }],
    executionLimit: 'always',
    priority: 0,
  };
}

function fromDTO(a: ModuleAutomationDTO): Draft {
  return {
    id: a.id,
    name: a.name,
    description: a.description ?? '',
    isActive: a.isActive,
    triggers: a.triggers?.length
      ? a.triggers
      : [{ trigger_type: 'entity_created', trigger_config: {} }],
    triggerLogic: a.triggerLogic === 'OR' ? 'OR' : 'AND',
    conditions: Array.isArray(a.conditionGroups?.[0]?.children)
      ? (a.conditionGroups[0]!.children as AutomationCondition[])
      : [],
    conditionLogic: a.conditionGroups?.[0]?.logic === 'OR' ? 'OR' : 'AND',
    actions: a.actions?.length ? a.actions : [{ type: 'send_notification', config: {} }],
    executionLimit: a.executionLimit ?? 'always',
    priority: a.priority ?? 0,
  };
}

/** Presety-szablony (klient) — prefill edytora, zapis dopiero po „Zapisz". */
const TEMPLATES: { nameKey: string; build: (t: TFunction) => Draft }[] = [
  {
    nameKey: 'moduleAutomations.templates.notifyNewRecord',
    build: (t) => ({
      ...emptyDraft(),
      name: t('moduleAutomations.templates.notifyNewRecord'),
      triggers: [{ trigger_type: 'entity_created', trigger_config: {} }],
      actions: [
        {
          type: 'send_notification',
          config: {
            to_field: 'custom',
            custom_email: '',
            title: t('moduleAutomations.templates.notifyNewRecordTitle'),
            message: t('moduleAutomations.templates.notifyNewRecordMessage'),
          },
        },
      ],
    }),
  },
  {
    nameKey: 'moduleAutomations.templates.setStatusOnCreate',
    build: (t) => ({
      ...emptyDraft(),
      name: t('moduleAutomations.templates.setStatusOnCreate'),
      triggers: [{ trigger_type: 'entity_created', trigger_config: {} }],
      actions: [{ type: 'update_field', config: { field: 'status', value: 'nowy' } }],
    }),
  },
  {
    nameKey: 'moduleAutomations.templates.webhookOnUpdate',
    build: (t) => ({
      ...emptyDraft(),
      name: t('moduleAutomations.templates.webhookOnUpdate'),
      triggers: [{ trigger_type: 'entity_updated', trigger_config: {} }],
      actions: [{ type: 'webhook', config: { url: '', method: 'POST' } }],
    }),
  },
  {
    nameKey: 'moduleAutomations.templates.dueDateReminder',
    build: (t) => ({
      ...emptyDraft(),
      name: t('moduleAutomations.templates.dueDateReminder'),
      triggers: [{ trigger_type: 'due_date_approaching', trigger_config: { field: '', days: 3 } }],
      executionLimit: 'daily',
      actions: [
        {
          type: 'send_notification',
          config: {
            to_field: 'custom',
            custom_email: '',
            title: t('moduleAutomations.templates.dueDateReminderTitle'),
            message: t('moduleAutomations.templates.dueDateReminderMessage'),
          },
        },
      ],
    }),
  },
];

/** Własny szablon tenanta (wiersz z /api/module-automation-templates). */
interface TemplateRow {
  id: string;
  name: string;
  description: string | null;
  definition: Record<string, unknown>;
}

function draftFromTemplate(row: TemplateRow): Draft {
  const def = row.definition ?? {};
  const triggers = Array.isArray(def.triggers) ? (def.triggers as AutomationTrigger[]) : [];
  const actions = Array.isArray(def.actions) ? (def.actions as AutomationAction[]) : [];
  const groups = Array.isArray(def.conditionGroups)
    ? (def.conditionGroups as AutomationCondition[])
    : [];
  return {
    ...emptyDraft(),
    name: row.name,
    description: row.description ?? '',
    triggers: triggers.length ? triggers : emptyDraft().triggers,
    triggerLogic: def.triggerLogic === 'OR' ? 'OR' : 'AND',
    conditions: Array.isArray(groups[0]?.children) ? (groups[0]!.children ?? []) : [],
    conditionLogic: groups[0]?.logic === 'OR' ? 'OR' : 'AND',
    actions: actions.length ? actions : emptyDraft().actions,
    executionLimit: (def.executionLimit as AutomationExecutionLimit) ?? 'always',
    priority: typeof def.priority === 'number' ? def.priority : 0,
  };
}

const inputCls =
  'w-full rounded-md border border-border-primary bg-bg-primary px-2.5 py-1.5 text-sm text-text-primary';
const labelCls = 'mb-1 block text-xs font-medium text-text-secondary';

export function AutomationsPanel({
  basePath,
  fields,
  moduleCode,
}: {
  /** Baza CRUD: `/modules/{id}/automations` (custom) albo `/system-modules/{slug}/automations`. */
  basePath: string;
  fields: ModuleField[];
  /**
   * Kod modułu (`custom.<slug>`) w kontekście tenanta — włącza listę wyboru procesów w akcji
   * „uruchom obieg". Brak (Kreator systemowy ITE) → pole tekstowe z ID procesu (fallback).
   */
  moduleCode?: string;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const queryKey = ['module-automations', basePath];

  const listQuery = useQuery({
    queryKey,
    queryFn: async () => {
      const res = await api.get<{ automations: ModuleAutomationDTO[] }>(basePath);
      return res.data.automations ?? [];
    },
  });

  const [draft, setDraft] = useState<Draft | null>(null);
  const [pendingDelete, setPendingDelete] = useState<ModuleAutomationDTO | null>(null);
  const [showTemplates, setShowTemplates] = useState(false);

  const invalidate = () => qc.invalidateQueries({ queryKey });

  const saveMutation = useMutation({
    mutationFn: async (d: Draft) => {
      const body = {
        name: d.name.trim(),
        description: d.description.trim() || null,
        isActive: d.isActive,
        triggers: d.triggers,
        triggerLogic: d.triggerLogic,
        conditionGroups: conditionsToGroups(d.conditions, d.conditionLogic),
        actions: d.actions,
        executionLimit: d.executionLimit,
        priority: d.priority,
      };
      if (d.id) return api.put(`${basePath}/${d.id}`, body);
      return api.post(basePath, body);
    },
    onSuccess: () => {
      toast.show({ title: t('moduleAutomations.toast.saved'), variant: 'success' });
      setDraft(null);
      invalidate();
    },
    onError: () => toast.show({ title: t('moduleAutomations.toast.saveError'), variant: 'error' }),
  });

  const toggleMutation = useMutation({
    mutationFn: async (a: ModuleAutomationDTO) =>
      api.put(`${basePath}/${a.id}`, { isActive: !a.isActive }),
    onSuccess: () => invalidate(),
    onError: () =>
      toast.show({ title: t('moduleAutomations.toast.toggleError'), variant: 'error' }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => api.delete(`${basePath}/${id}`),
    onSuccess: () => {
      toast.show({ title: t('moduleAutomations.toast.deleted'), variant: 'success' });
      setPendingDelete(null);
      invalidate();
    },
    onError: () =>
      toast.show({ title: t('moduleAutomations.toast.deleteError'), variant: 'error' }),
  });

  // Własne szablony tenanta (tenant-globalne, wspólne dla custom i systemowych modułów).
  const templatesKey = ['module-automation-templates'];
  const templatesQuery = useQuery({
    queryKey: templatesKey,
    queryFn: async () => {
      const res = await api.get<{ templates: TemplateRow[] }>('/module-automation-templates');
      return res.data.templates ?? [];
    },
  });
  const customTemplates = templatesQuery.data ?? [];

  const saveTemplateMutation = useMutation({
    mutationFn: async (d: Draft) =>
      api.post('/module-automation-templates', {
        name: d.name.trim() || t('moduleAutomations.defaultTemplateName'),
        description: d.description.trim() || null,
        definition: {
          triggers: d.triggers,
          triggerLogic: d.triggerLogic,
          conditionGroups: conditionsToGroups(d.conditions, d.conditionLogic),
          actions: d.actions,
          executionLimit: d.executionLimit,
          priority: d.priority,
        },
      }),
    onSuccess: () => {
      toast.show({ title: t('moduleAutomations.toast.templateSaved'), variant: 'success' });
      qc.invalidateQueries({ queryKey: templatesKey });
    },
    onError: () =>
      toast.show({ title: t('moduleAutomations.toast.templateSaveError'), variant: 'error' }),
  });

  const deleteTemplateMutation = useMutation({
    mutationFn: async (id: string) => api.delete(`/module-automation-templates/${id}`),
    onSuccess: () => {
      toast.show({ title: t('moduleAutomations.toast.templateDeleted'), variant: 'success' });
      qc.invalidateQueries({ queryKey: templatesKey });
    },
    onError: () =>
      toast.show({ title: t('moduleAutomations.toast.templateDeleteError'), variant: 'error' }),
  });

  const list = listQuery.data ?? [];

  /* ---- mutatory draftu ---- */
  const patchDraft = (p: Partial<Draft>) => setDraft((d) => (d ? { ...d, ...p } : d));
  const patchTrigger = (i: number, tp: Partial<AutomationTrigger>) =>
    setDraft((d) =>
      d ? { ...d, triggers: d.triggers.map((tr, idx) => (idx === i ? { ...tr, ...tp } : tr)) } : d,
    );
  const patchTriggerCfg = (i: number, cfg: Record<string, unknown>) =>
    patchTrigger(i, {
      trigger_config: { ...(draft?.triggers[i]?.trigger_config ?? {}), ...cfg },
    });
  const patchAction = (i: number, a: Partial<AutomationAction>) =>
    setDraft((d) =>
      d ? { ...d, actions: d.actions.map((ac, idx) => (idx === i ? { ...ac, ...a } : ac)) } : d,
    );
  const patchActionCfg = (i: number, cfg: Record<string, unknown>) =>
    patchAction(i, { config: { ...(draft?.actions[i]?.config ?? {}), ...cfg } });
  const patchCondition = (i: number, c: Partial<AutomationCondition>) =>
    setDraft((d) =>
      d
        ? { ...d, conditions: d.conditions.map((cc, idx) => (idx === i ? { ...cc, ...c } : cc)) }
        : d,
    );

  const canSave =
    draft && draft.name.trim() && draft.triggers.length > 0 && draft.actions.length > 0;

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[320px_1fr]">
      {/* Lewa kolumna — lista */}
      <div className="space-y-2">
        <Button
          variant="primary"
          size="sm"
          leftIcon={<Plus className="h-4 w-4" />}
          onClick={() => {
            setShowTemplates(false);
            setDraft(withUids(emptyDraft()));
          }}
          className="w-full"
        >
          {t('moduleAutomations.newAutomation')}
        </Button>
        <div className="relative">
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Wand2 className="h-4 w-4" />}
            onClick={() => setShowTemplates((v) => !v)}
            className="w-full"
          >
            {t('moduleAutomations.useTemplate')}
          </Button>
          {showTemplates && (
            <div className="absolute z-10 mt-1 max-h-72 w-full overflow-y-auto rounded-lg border border-border-primary bg-bg-elevated shadow-lg">
              {TEMPLATES.map((tpl) => (
                <button
                  key={tpl.nameKey}
                  type="button"
                  className="block w-full px-3 py-2 text-left text-sm text-text-primary hover:bg-bg-tertiary"
                  onClick={() => {
                    setDraft(withUids(tpl.build(t)));
                    setShowTemplates(false);
                  }}
                >
                  {t(tpl.nameKey)}
                </button>
              ))}
              {customTemplates.length > 0 && (
                <div className="border-t border-border-primary px-3 py-1 text-[11px] font-medium uppercase text-text-muted">
                  {t('moduleAutomations.yourTemplates')}
                </div>
              )}
              {customTemplates.map((tpl) => (
                <div key={tpl.id} className="flex items-center hover:bg-bg-tertiary">
                  <button
                    type="button"
                    className="block flex-1 truncate px-3 py-2 text-left text-sm text-text-primary"
                    onClick={() => {
                      setDraft(withUids(draftFromTemplate(tpl)));
                      setShowTemplates(false);
                    }}
                  >
                    {tpl.name}
                  </button>
                  <button
                    type="button"
                    title={t('moduleAutomations.deleteTemplate')}
                    className="shrink-0 px-2 text-danger-600 hover:text-danger-700"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteTemplateMutation.mutate(tpl.id);
                    }}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-2 space-y-1.5">
          {listQuery.isLoading && (
            <p className="px-1 text-sm text-text-muted">{t('moduleAutomations.loading')}</p>
          )}
          {!listQuery.isLoading && list.length === 0 && (
            <div className="rounded-lg border border-dashed border-border-primary p-4 text-center text-sm text-text-muted">
              {t('moduleAutomations.noAutomations')}
            </div>
          )}
          {list.map((a) => (
            <div
              key={a.id}
              className={[
                'flex items-start gap-2 rounded-lg border p-2.5 cursor-pointer transition-colors',
                draft?.id === a.id
                  ? 'border-accent-400 bg-accent-50 dark:bg-accent-900/20'
                  : 'border-border-primary hover:bg-bg-tertiary',
              ].join(' ')}
              onClick={() => setDraft(withUids(fromDTO(a)))}
              data-testid={`automation-row-${a.id}`}
            >
              <Zap
                className={`mt-0.5 h-4 w-4 shrink-0 ${a.isActive ? 'text-accent-600' : 'text-text-muted'}`}
              />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-text-primary">{a.name}</div>
                <div className="truncate text-xs text-text-muted">
                  {(a.triggers ?? [])
                    .map((trg) => t(TRIGGER_LABELS[trg.trigger_type] ?? trg.trigger_type))
                    .join(
                      a.triggerLogic === 'OR'
                        ? t('moduleAutomations.orSeparator')
                        : t('moduleAutomations.andSeparator'),
                    ) || '—'}
                </div>
              </div>
              <button
                type="button"
                title={a.isActive ? t('moduleAutomations.disable') : t('moduleAutomations.enable')}
                className="shrink-0 rounded p-1 text-text-secondary hover:bg-bg-primary"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleMutation.mutate(a);
                }}
              >
                {a.isActive ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Prawa kolumna — edytor */}
      <div className="rounded-xl border border-border-primary bg-bg-elevated p-4">
        {!draft ? (
          <div className="flex h-64 flex-col items-center justify-center text-center text-text-muted">
            <Zap className="mb-2 h-8 w-8" />
            <p className="text-sm">{t('moduleAutomations.selectOrCreate')}</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-base font-semibold text-text-primary">
                {draft.id
                  ? t('moduleAutomations.editAutomation')
                  : t('moduleAutomations.newAutomation')}
              </h3>
              <label className="flex items-center gap-2 text-sm text-text-secondary">
                <input
                  type="checkbox"
                  checked={draft.isActive}
                  onChange={(e) => patchDraft({ isActive: e.target.checked })}
                  className="h-4 w-4 rounded border-border-primary text-accent-600"
                />
                {t('moduleAutomations.active')}
              </label>
            </div>

            <div>
              <label className={labelCls}>{t('moduleAutomations.name')}</label>
              <input
                type="text"
                value={draft.name}
                onChange={(e) => patchDraft({ name: e.target.value })}
                placeholder={t('moduleAutomations.ph.name')}
                className={inputCls}
              />
            </div>

            {/* Wyzwalacze */}
            <div>
              <div className="mb-1 flex items-center justify-between">
                <span className={labelCls + ' mb-0'}>{t('moduleAutomations.triggersLabel')}</span>
                {draft.triggers.length > 1 && (
                  <Dropdown
                    value={draft.triggerLogic}
                    onChange={(v) => patchDraft({ triggerLogic: v as 'AND' | 'OR' })}
                    className="rounded border border-border-primary bg-bg-primary px-1.5 py-0.5 text-xs"
                    options={[
                      { value: 'AND', label: t('moduleAutomations.logic.allAnd') },
                      { value: 'OR', label: t('moduleAutomations.logic.anyOr') },
                    ]}
                  />
                )}
              </div>
              <div className="space-y-2">
                {draft.triggers.map((tr, i) => (
                  <div key={tr._uid ?? i} className="rounded-md border border-border-primary p-2">
                    <div className="flex items-center gap-2">
                      <Dropdown
                        value={tr.trigger_type}
                        onChange={(v) =>
                          patchTrigger(i, {
                            trigger_type: v as AutomationTriggerType,
                            trigger_config: {},
                          })
                        }
                        className={inputCls}
                        options={(Object.keys(TRIGGER_LABELS) as AutomationTriggerType[]).map(
                          (tt) => ({ value: tt, label: t(TRIGGER_LABELS[tt]) }),
                        )}
                      />
                      {draft.triggers.length > 1 && (
                        <button
                          type="button"
                          onClick={() =>
                            patchDraft({ triggers: draft.triggers.filter((_, idx) => idx !== i) })
                          }
                          className="shrink-0 rounded p-1 text-danger-600 hover:bg-bg-tertiary"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    {FIELD_TRIGGERS.includes(tr.trigger_type) && (
                      <div className="mt-2 grid grid-cols-2 gap-2">
                        <Dropdown
                          value={String(tr.trigger_config?.field ?? '')}
                          onChange={(v) => patchTriggerCfg(i, { field: v })}
                          className={inputCls}
                          options={[
                            { value: '', label: t('moduleAutomations.fieldOption') },
                            ...fields.map((f) => ({ value: f.slug, label: f.name })),
                          ]}
                        />
                        {tr.trigger_type === 'field_equals' && (
                          <input
                            type="text"
                            placeholder={t('moduleAutomations.ph.value')}
                            value={String(tr.trigger_config?.value ?? '')}
                            onChange={(e) => patchTriggerCfg(i, { value: e.target.value })}
                            className={inputCls}
                          />
                        )}
                        {tr.trigger_type === 'status_changed' && (
                          <input
                            type="text"
                            placeholder={t('moduleAutomations.ph.newStatus')}
                            value={String(tr.trigger_config?.status_to ?? '')}
                            onChange={(e) => patchTriggerCfg(i, { status_to: e.target.value })}
                            className={inputCls}
                          />
                        )}
                      </div>
                    )}
                    {TIME_TRIGGERS.includes(tr.trigger_type) && (
                      <>
                        <div className="mt-2 grid grid-cols-2 gap-2">
                          {DUE_DATE_TRIGGERS.includes(tr.trigger_type) && (
                            <Dropdown
                              value={String(tr.trigger_config?.field ?? '')}
                              onChange={(v) => patchTriggerCfg(i, { field: v })}
                              className={inputCls}
                              options={[
                                { value: '', label: t('moduleAutomations.dateFieldOption') },
                                ...fields.map((f) => ({ value: f.slug, label: f.name })),
                              ]}
                            />
                          )}
                          {tr.trigger_type === 'stale_entity' && (
                            <input
                              type="number"
                              min={1}
                              placeholder={t('moduleAutomations.ph.daysStale')}
                              value={String(tr.trigger_config?.days ?? '')}
                              onChange={(e) =>
                                patchTriggerCfg(i, { days: Number(e.target.value) || undefined })
                              }
                              className={inputCls}
                            />
                          )}
                          {tr.trigger_type === 'due_date_approaching' && (
                            <input
                              type="number"
                              min={1}
                              placeholder={t('moduleAutomations.ph.daysBefore')}
                              value={String(tr.trigger_config?.days ?? '')}
                              onChange={(e) =>
                                patchTriggerCfg(i, { days: Number(e.target.value) || undefined })
                              }
                              className={inputCls}
                            />
                          )}
                          {tr.trigger_type === 'due_date_passed' && (
                            <input
                              type="number"
                              min={0}
                              placeholder={t('moduleAutomations.ph.graceDays')}
                              value={String(tr.trigger_config?.grace_days ?? '')}
                              onChange={(e) =>
                                patchTriggerCfg(i, {
                                  grace_days: Number(e.target.value) || undefined,
                                })
                              }
                              className={inputCls}
                            />
                          )}
                          {tr.trigger_type === 'time_based' && (
                            <Dropdown
                              value={String(tr.trigger_config?.schedule ?? 'daily')}
                              onChange={(v) => patchTriggerCfg(i, { schedule: v })}
                              className={inputCls}
                              options={[
                                { value: 'hourly', label: t('moduleAutomations.schedules.hourly') },
                                { value: 'daily', label: t('moduleAutomations.schedules.daily') },
                                { value: 'weekly', label: t('moduleAutomations.schedules.weekly') },
                                {
                                  value: 'monthly',
                                  label: t('moduleAutomations.schedules.monthly'),
                                },
                              ]}
                            />
                          )}
                        </div>
                        <p className="mt-1 text-xs text-text-tertiary">
                          {t('moduleAutomations.timeTriggerHint')}
                        </p>
                      </>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() =>
                    patchDraft({
                      triggers: [
                        ...draft.triggers,
                        { trigger_type: 'entity_updated', trigger_config: {}, _uid: nextUid() },
                      ],
                    })
                  }
                  className="text-sm font-medium text-accent-600 hover:underline"
                >
                  {t('moduleAutomations.addTrigger')}
                </button>
              </div>
            </div>

            {/* Warunki (opcjonalne) */}
            <div>
              <div className="mb-1 flex items-center justify-between">
                <span className={labelCls}>{t('moduleAutomations.conditionsOptional')}</span>
                {draft.conditions.length > 1 && (
                  <Dropdown
                    value={draft.conditionLogic}
                    onChange={(v) => patchDraft({ conditionLogic: v as 'AND' | 'OR' })}
                    className="rounded border border-border-primary bg-bg-primary px-1.5 py-0.5 text-xs text-text-primary"
                    options={[
                      { value: 'AND', label: t('moduleAutomations.allMatch') },
                      { value: 'OR', label: t('moduleAutomations.anyMatch') },
                    ]}
                  />
                )}
              </div>
              <div className="space-y-2">
                {draft.conditions.map((c, i) => {
                  const op = (c.operator ?? '==') as AutomationConditionOperator;
                  return (
                    <div key={c._uid ?? i} className="flex items-center gap-2">
                      <Dropdown
                        value={c.field ?? ''}
                        onChange={(v) => patchCondition(i, { field: v })}
                        className={inputCls}
                        options={[
                          { value: '', label: t('moduleAutomations.fieldOption') },
                          ...fields.map((f) => ({ value: f.slug, label: f.name })),
                        ]}
                      />
                      <Dropdown
                        value={op}
                        onChange={(v) =>
                          patchCondition(i, {
                            operator: v as AutomationConditionOperator,
                          })
                        }
                        className={inputCls}
                        options={AUTOMATION_CONDITION_OPERATORS.map((o) => ({
                          value: o,
                          label: t(OPERATOR_LABELS[o]),
                        }))}
                      />
                      {!VALUELESS_OPERATORS.includes(op) && (
                        <input
                          type="text"
                          placeholder={t('moduleAutomations.ph.value')}
                          value={String(c.value ?? '')}
                          onChange={(e) => patchCondition(i, { value: e.target.value })}
                          className={inputCls}
                        />
                      )}
                      <button
                        type="button"
                        onClick={() =>
                          patchDraft({ conditions: draft.conditions.filter((_, idx) => idx !== i) })
                        }
                        className="shrink-0 rounded p-1 text-danger-600 hover:bg-bg-tertiary"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  );
                })}
                <button
                  type="button"
                  onClick={() =>
                    patchDraft({
                      conditions: [
                        ...draft.conditions,
                        {
                          field: fields[0]?.slug ?? '',
                          operator: '==',
                          value: '',
                          _uid: nextUid(),
                        },
                      ],
                    })
                  }
                  className="text-sm font-medium text-accent-600 hover:underline"
                >
                  {t('moduleAutomations.addCondition')}
                </button>
              </div>
            </div>

            {/* Akcje */}
            <div>
              <span className={labelCls}>{t('moduleAutomations.actionsLabel')}</span>
              <div className="space-y-2">
                {draft.actions.map((ac, i) => (
                  <div key={ac._uid ?? i} className="rounded-md border border-border-primary p-2">
                    <div className="flex items-center gap-2">
                      <Dropdown
                        value={ac.type}
                        onChange={(v) =>
                          patchAction(i, {
                            type: v as AutomationActionType,
                            config: {},
                          })
                        }
                        className={inputCls}
                        options={(Object.keys(ACTION_LABELS) as AutomationActionType[]).map(
                          (at) => ({ value: at, label: t(ACTION_LABELS[at]) }),
                        )}
                      />
                      {draft.actions.length > 1 && (
                        <button
                          type="button"
                          onClick={() =>
                            patchDraft({ actions: draft.actions.filter((_, idx) => idx !== i) })
                          }
                          className="shrink-0 rounded p-1 text-danger-600 hover:bg-bg-tertiary"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    <ActionConfig
                      action={ac}
                      fields={fields}
                      basePath={basePath}
                      moduleCode={moduleCode}
                      onChange={(cfg) => patchActionCfg(i, cfg)}
                    />
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() =>
                    patchDraft({
                      actions: [
                        ...draft.actions,
                        { type: 'update_field', config: {}, _uid: nextUid() },
                      ],
                    })
                  }
                  className="text-sm font-medium text-accent-600 hover:underline"
                >
                  {t('moduleAutomations.addAction')}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className={labelCls}>{t('moduleAutomations.executionLimit')}</label>
                <Dropdown
                  value={draft.executionLimit}
                  onChange={(v) => patchDraft({ executionLimit: v as AutomationExecutionLimit })}
                  className={inputCls}
                  options={(Object.keys(EXECUTION_LIMIT_LABELS) as AutomationExecutionLimit[]).map(
                    (l) => ({ value: l, label: t(EXECUTION_LIMIT_LABELS[l]) }),
                  )}
                />
              </div>
              <div>
                <label className={labelCls}>{t('moduleAutomations.priority')}</label>
                <input
                  type="number"
                  value={draft.priority}
                  onChange={(e) => patchDraft({ priority: Number(e.target.value) || 0 })}
                  className={inputCls}
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-border-primary pt-3">
              {draft.id && (
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={<Copy className="h-4 w-4" />}
                  onClick={() =>
                    setDraft({
                      ...draft,
                      id: undefined,
                      name: t('moduleAutomations.copySuffix', { name: draft.name }),
                      isActive: false,
                    })
                  }
                >
                  {t('moduleAutomations.duplicate')}
                </Button>
              )}
              {draft.id && (
                <Button
                  variant="danger"
                  size="sm"
                  leftIcon={<Trash2 className="h-4 w-4" />}
                  onClick={() => {
                    const found = list.find((a) => a.id === draft.id);
                    if (found) setPendingDelete(found);
                  }}
                >
                  {t('moduleAutomations.delete')}
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<Wand2 className="h-4 w-4" />}
                disabled={!canSave || saveTemplateMutation.isPending}
                onClick={() => draft && saveTemplateMutation.mutate(draft)}
              >
                {t('moduleAutomations.saveAsTemplate')}
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setDraft(null)}>
                {t('moduleAutomations.cancel')}
              </Button>
              <Button
                variant="primary"
                size="sm"
                disabled={!canSave || saveMutation.isPending}
                loading={saveMutation.isPending}
                onClick={() => draft && saveMutation.mutate(draft)}
              >
                {t('moduleAutomations.save')}
              </Button>
            </div>

            {draft.id && <ExecutionHistory basePath={basePath} automationId={draft.id} />}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={pendingDelete !== null}
        title={t('moduleAutomations.confirmDelete.title')}
        message={
          pendingDelete
            ? t('moduleAutomations.confirmDelete.message', { name: pendingDelete.name })
            : ''
        }
        confirmLabel={t('moduleAutomations.delete')}
        variant="danger"
        onConfirm={() => {
          if (pendingDelete) deleteMutation.mutate(pendingDelete.id);
          setDraft(null);
        }}
        onClose={() => setPendingDelete(null)}
      />
    </div>
  );
}

/** Konfiguracja pojedynczej akcji (per typ). */
function ActionConfig({
  action,
  fields,
  basePath,
  moduleCode,
  onChange,
}: {
  action: AutomationAction;
  fields: ModuleField[];
  basePath: string;
  moduleCode?: string;
  onChange: (cfg: Record<string, unknown>) => void;
}) {
  const { t } = useTranslation();
  const cfg = (action.config ?? {}) as Record<string, unknown>;
  const s = (k: string) => String(cfg[k] ?? '');
  // Listy wyboru mają sens tylko u tenanta (konkretni użytkownicy / moduły / procesy).
  // Obie trasy panelu są tenantowe: `/modules/:id/automations` (moduł własny) i
  // `/system-modules/:slug/automations` (kartoteka systemowa w Kreatorze Listy tenanta —
  // zakładka jest tylko w scope `system`, którego używa wyłącznie panel klienta).
  const isTenant = basePath.startsWith('/modules/') || basePath.startsWith('/system-modules/');

  // Moduły docelowe dla akcji „utwórz rekord" (tenant).
  const modulesQuery = useQuery({
    queryKey: ['automation-modules-picker'],
    enabled: isTenant && action.type === 'create_record',
    staleTime: 120_000,
    queryFn: async () => {
      const res = await api.get<{ modules: Array<{ id: string; slug: string; name: string }> }>(
        '/modules',
      );
      return res.data.modules ?? [];
    },
  });

  // Opublikowane procesy modułu dla akcji „uruchom obieg" (tenant + znany kod modułu).
  const processesQuery = useQuery({
    queryKey: ['processes', 'for-module', moduleCode],
    enabled: isTenant && action.type === 'start_approval' && !!moduleCode,
    staleTime: 120_000,
    queryFn: () => fetchProcessesForModule(moduleCode!),
  });

  if (action.type === 'update_field' || action.type === 'assign_owner') {
    return (
      <div className="mt-2 grid grid-cols-2 gap-2">
        <Dropdown
          value={s('field')}
          onChange={(v) => onChange({ field: v })}
          className={inputCls}
          options={[
            { value: '', label: t('moduleAutomations.fieldOption') },
            ...fields.map((f) => ({ value: f.slug, label: f.name })),
          ]}
        />
        {action.type === 'assign_owner' && isTenant ? (
          <UserPicker
            value={s('user_id') || null}
            onChange={(v) => onChange({ user_id: v ?? '' })}
            placeholder={t('moduleAutomations.ph.selectOwner')}
          />
        ) : (
          <input
            type="text"
            placeholder={
              action.type === 'assign_owner'
                ? t('moduleAutomations.ph.userId')
                : t('moduleAutomations.ph.value')
            }
            value={action.type === 'assign_owner' ? s('user_id') : s('value')}
            onChange={(e) =>
              onChange(
                action.type === 'assign_owner'
                  ? { user_id: e.target.value }
                  : { value: e.target.value },
              )
            }
            className={inputCls}
          />
        )}
      </div>
    );
  }

  if (action.type === 'send_email' || action.type === 'send_notification') {
    return (
      <div className="mt-2 space-y-2">
        <input
          type="text"
          placeholder={t('moduleAutomations.ph.emailAddress')}
          value={s('custom_email') || s('to')}
          onChange={(e) => onChange({ to_field: 'custom', custom_email: e.target.value })}
          className={inputCls}
        />
        <input
          type="text"
          placeholder={t('moduleAutomations.ph.subject')}
          value={s('subject') || s('title')}
          onChange={(e) => onChange({ subject: e.target.value, title: e.target.value })}
          className={inputCls}
        />
        <textarea
          placeholder={t('moduleAutomations.ph.body')}
          rows={2}
          value={s('body_html') || s('message')}
          onChange={(e) => onChange({ body_html: e.target.value, message: e.target.value })}
          className={inputCls}
        />
      </div>
    );
  }

  if (action.type === 'webhook') {
    return (
      <div className="mt-2 grid grid-cols-[1fr_100px] gap-2">
        <input
          type="text"
          placeholder="https://…"
          value={s('url')}
          onChange={(e) => onChange({ url: e.target.value })}
          className={inputCls}
        />
        <Dropdown
          value={s('method') || 'POST'}
          onChange={(v) => onChange({ method: v })}
          className={inputCls}
          options={[
            { value: 'POST', label: 'POST' },
            { value: 'GET', label: 'GET' },
            { value: 'PUT', label: 'PUT' },
          ]}
        />
      </div>
    );
  }

  if (action.type === 'create_record') {
    const fv = (cfg.field_values ?? {}) as Record<string, unknown>;
    const modules = modulesQuery.data ?? [];
    return (
      <div className="mt-2 space-y-2">
        {isTenant ? (
          <Dropdown
            value={s('target_module_slug')}
            onChange={(v) => onChange({ target_module_slug: v })}
            className={inputCls}
            options={[
              { value: '', label: t('moduleAutomations.ph.selectModule') },
              ...modules.map((m) => ({ value: m.slug, label: m.name })),
            ]}
          />
        ) : (
          <input
            type="text"
            placeholder={t('moduleAutomations.ph.targetModuleSlug')}
            value={s('target_module_slug')}
            onChange={(e) => onChange({ target_module_slug: e.target.value })}
            className={inputCls}
          />
        )}
        <input
          type="text"
          placeholder={t('moduleAutomations.ph.newRecordName')}
          value={String(fv.record_name ?? '')}
          onChange={(e) => onChange({ field_values: { ...fv, record_name: e.target.value } })}
          className={inputCls}
        />
      </div>
    );
  }

  if (action.type === 'start_approval') {
    const processes = processesQuery.data ?? [];
    if (isTenant && moduleCode) {
      return (
        <Dropdown
          value={s('process_id')}
          onChange={(v) => onChange({ process_id: v })}
          className={`${inputCls} mt-2`}
          options={[
            { value: '', label: t('moduleAutomations.ph.selectProcess') },
            ...processes.map((p) => ({ value: p.id, label: p.name })),
          ]}
        />
      );
    }
    return (
      <input
        type="text"
        placeholder={t('moduleAutomations.ph.processId')}
        value={s('process_id')}
        onChange={(e) => onChange({ process_id: e.target.value })}
        className={`${inputCls} mt-2`}
      />
    );
  }

  if (action.type === 'add_tag' || action.type === 'remove_tag') {
    return (
      <div className="mt-2 grid grid-cols-2 gap-2">
        <input
          type="text"
          placeholder={t('moduleAutomations.ph.tagField')}
          value={s('field')}
          onChange={(e) => onChange({ field: e.target.value })}
          className={inputCls}
        />
        <input
          type="text"
          placeholder="tag"
          value={s('tag')}
          onChange={(e) => onChange({ tag: e.target.value })}
          className={inputCls}
        />
      </div>
    );
  }

  if (action.type === 'archive' || action.type === 'unarchive') {
    return <p className="mt-2 text-xs text-text-tertiary">{t('moduleAutomations.noConfigHint')}</p>;
  }

  return null;
}

const LOG_STATUS_LABELS: Record<string, string> = {
  success: 'moduleAutomations.status.success',
  failed: 'moduleAutomations.status.failed',
  skipped: 'moduleAutomations.status.skipped',
};
const LOG_STATUS_CLS: Record<string, string> = {
  success: 'text-success-600',
  failed: 'text-danger-600',
  skipped: 'text-text-muted',
};

/** Historia wykonań reguły (F12.D4) — ostatnie wpisy logu z endpointu `/logs`. */
function ExecutionHistory({ basePath, automationId }: { basePath: string; automationId: string }) {
  const { t } = useTranslation();
  const logsQuery = useQuery({
    queryKey: ['module-automation-logs', basePath, automationId],
    queryFn: async () => {
      const res = await api.get<{ logs: ModuleAutomationLogDTO[] }>(
        `${basePath}/${automationId}/history`,
      );
      return res.data.logs ?? [];
    },
  });
  const logs = logsQuery.data ?? [];

  return (
    <div className="border-t border-border-primary pt-3">
      <span className={labelCls}>{t('moduleAutomations.executionHistory')}</span>
      {logsQuery.isLoading && (
        <p className="text-xs text-text-muted">{t('moduleAutomations.loading')}</p>
      )}
      {!logsQuery.isLoading && logs.length === 0 && (
        <p className="text-xs text-text-muted">{t('moduleAutomations.noExecutions')}</p>
      )}
      <div className="max-h-52 space-y-1 overflow-y-auto">
        {logs.map((l) => {
          const okCount = l.actionsExecuted?.filter((a) => a.success).length ?? 0;
          const total = l.actionsExecuted?.length ?? 0;
          const statusKey = LOG_STATUS_LABELS[l.status];
          return (
            <div
              key={l.id}
              className="flex items-center gap-2 rounded border border-border-primary px-2 py-1 text-xs"
            >
              <span className={`shrink-0 font-medium ${LOG_STATUS_CLS[l.status] ?? ''}`}>
                {statusKey ? t(statusKey) : l.status}
              </span>
              <span className="shrink-0 text-text-muted">
                {new Date(l.executedAt).toLocaleString('pl-PL')}
              </span>
              <span className="truncate text-text-secondary">
                {l.triggerType ?? ''}
                {total > 0 ? t('moduleAutomations.logActions', { ok: okCount, total }) : ''}
                {l.errorMessage ? ` — ${l.errorMessage}` : ''}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default AutomationsPanel;
